import { useId, useState } from "react";
import { Badge } from "../../design-v2";
import { ChevronDownGlyph } from "./settingsGlyphs";

// Kept as a Settings-owned disclosure rather than the canonical `Disclosure`
// primitive: the canonical panel stays mounted and merely hidden, while this
// block unmounts its content. Equipment categories and Advanced signals depend
// on that unmount (only the open category's controls exist), so replacing it
// would change existing behaviour, and behaviour wins over component purity.
export default function CollapsibleBlock({
  title,
  description,
  badge,
  defaultOpen = false,
  isOpen: controlledIsOpen,
  onToggle,
  children,
}) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
  const isControlled = typeof controlledIsOpen === "boolean";
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;
  const generatedId = useId().replace(/:/g, "");
  const triggerId = `lz-settings-collapsible-trigger-${generatedId}`;
  const panelId = `lz-settings-collapsible-panel-${generatedId}`;

  function handleToggle() {
    if (isControlled) {
      onToggle?.();
      return;
    }

    setUncontrolledIsOpen((currentValue) => !currentValue);
  }

  return (
    <div
      className={[
        "lz-settings-collapsible",
        isOpen ? "lz-settings-collapsible--open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        id={triggerId}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={handleToggle}
        className="lz-settings-collapsible__trigger"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="lz-settings-collapsible__title">{title}</span>
            {badge ? <Badge tone="neutral">{badge}</Badge> : null}
          </span>
          {description ? (
            <span className="lz-settings-section__description block">{description}</span>
          ) : null}
        </span>

        <ChevronDownGlyph className="lz-settings-collapsible__icon" />
      </button>

      {isOpen ? (
        <div
          className="lz-settings-collapsible__panel"
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
